import { CheckCircleOutlineRounded, SyncRounded, WarningAmberRounded } from "@mui/icons-material";
import { Box, Button, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useState } from "react";
import FinanceInvoicesPanel from "@/components/finance/FinanceInvoicesPanel";
import InvoiceSyncDialog from "@/components/finance/InvoiceSyncDialog";
import SubscriptionInvoicesPanel from "@/components/finance/SubscriptionInvoicesPanel";
import { useInvoiceSyncStatus } from "@/hooks/useInvoiceSync";
import { usePermissions } from "@/hooks/usePermissions";
import type { InvoiceSyncStatus } from "@/interfaces/invoiceSync";
import { formatDateTime } from "@/utils/date-format";

type InvoiceType = "credits" | "subscriptions";

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

function describe(lastRun: InvoiceSyncStatus["lastRun"]): { ok: boolean; text: string } {
  if (!lastRun) return { ok: true, text: "The daily Stripe sync has not run yet." };
  const when = formatDateTime(lastRun.finishedAt ?? lastRun.startedAt);
  if (lastRun.status === "running")
    return { ok: true, text: `The daily Stripe sync is running (started ${when}).` };
  if (lastRun.status === "failed") {
    return {
      ok: false,
      text: `The daily Stripe sync on ${when} failed${lastRun.failed ? ` on ${plural(lastRun.failed, "invoice")}` : ""}. ${lastRun.error ?? ""}`.trim(),
    };
  }
  const fixed = lastRun.fixed
    ? `fixed ${plural(lastRun.fixed, "invoice")}`
    : "found everything in step";
  return lastRun.reported
    ? {
        ok: false,
        text: `The daily Stripe sync ran ${when} and ${fixed}; ${plural(lastRun.reported, "invoice")} need a person. Run Sync with Stripe to see them.`,
      }
    : { ok: true, text: `The daily Stripe sync ran ${when} and ${fixed}.` };
}

/** Last daily sync, with Sync with Stripe for whoever may fix invoices. GFG finance only. */
function SyncBar({ canSync }: { canSync: boolean }) {
  const { data } = useInvoiceSyncStatus();
  const [open, setOpen] = useState(false);
  const { ok, text } = data ? describe(data.lastRun) : { ok: true, text: "" };
  return (
    <Box
      component="section"
      aria-label="Stripe sync"
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 2,
        mb: 2,
        px: 2,
        py: 1.25,
        borderRadius: "var(--radius-card)",
        border: `1px solid ${ok ? "var(--color-card-border)" : "var(--color-good-ochre)"}`,
        background: ok ? "var(--color-white)" : "var(--color-good-ochre-bg)",
      }}
    >
      {ok ? (
        <CheckCircleOutlineRounded fontSize="small" sx={{ color: "var(--color-good-sage)" }} />
      ) : (
        <WarningAmberRounded fontSize="small" sx={{ color: "var(--color-good-ochre)" }} />
      )}
      <Typography sx={{ flex: "1 1 360px", minWidth: 0, fontSize: 13 }}>{text}</Typography>
      {canSync && (
        <Button
          size="small"
          variant="contained"
          startIcon={<SyncRounded fontSize="small" />}
          onClick={() => setOpen(true)}
          sx={{
            textTransform: "none",
            background: "var(--color-rodeo-black)",
            "&:hover": { background: "var(--color-rodeo-black)", opacity: 0.9 },
          }}
        >
          Sync with Stripe
        </Button>
      )}
      <InvoiceSyncDialog open={open} onClose={() => setOpen(false)} />
    </Box>
  );
}

/**
 * Finance › Invoices. Everyone with the tab sees credit invoices (row-scoped for clients); GFG finance
 * (FINANCE/view) also gets the Credits | Subscriptions switch and the Stripe sync status, and those
 * who can edit finance get Sync with Stripe.
 */
export default function FinanceInvoicesTab() {
  const { can } = usePermissions();
  const isFinance = can("view", "FINANCE");
  const [type, setType] = useState<InvoiceType>("credits");

  if (!isFinance) return <FinanceInvoicesPanel />;

  return (
    <Box>
      <SyncBar canSync={can("edit", "FINANCE")} />
      <ToggleButtonGroup
        exclusive
        size="small"
        value={type}
        onChange={(_, v: InvoiceType | null) => v && setType(v)}
        aria-label="Invoice type"
        sx={{
          mb: 2,
          "& .MuiToggleButton-root": {
            textTransform: "none",
            fontFamily: "var(--font-lato)",
            fontSize: 13.5,
            fontWeight: 700,
            px: 2.5,
            minHeight: 40,
            color: "var(--color-graphite)",
            borderColor: "var(--color-smoke)",
          },
          "& .Mui-selected": {
            color: "var(--color-white) !important",
            background: "var(--color-rodeo-black) !important",
          },
        }}
      >
        <ToggleButton value="credits">Credits</ToggleButton>
        <ToggleButton value="subscriptions">Subscriptions</ToggleButton>
      </ToggleButtonGroup>
      {type === "credits" ? <FinanceInvoicesPanel /> : <SubscriptionInvoicesPanel />}
    </Box>
  );
}
